/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { InputEventHandler, KeyboardEventHandler } from "react";
import React, { forwardRef, useRef, useState } from "react";
import * as RPNInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";

import type { ThemeType } from "../../../../../global/emotion";
import { numericPercentages } from "../../../../../global/emotion";
import { TextInput } from "../TextInput/TextInput.emotion";
import { PhoneCountryFlag } from "./PhoneCountryFlag.emotion";
import { PhoneCountrySelect } from "./PhoneCountrySelect.emotion";

const baseStyle: Interpolation = {
  display: "flex",
  justifyItems: "stretch",
  width: numericPercentages[100],
};
const inputStyle: Interpolation<ThemeType> = (theme) => ({
  borderBottomLeftRadius: "0px",
  borderLeftColor: "transparent",
  borderTopLeftRadius: "0px",
  flexGrow: 1,
  ":hover:not(:disabled)": {
    borderLeftColor: theme.semanticColors.neutralsVeryLow,
  },
  ":focus": {
    borderLeftColor: `${theme.semanticColors.primary} !important`,
  },
});

export type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> & {
  locale?: string;
  required?: boolean;
  inputComponent?: React.ElementType<any, keyof React.JSX.IntrinsicElements>;
  countrySelectComponent?: React.ElementType<
    any,
    keyof React.JSX.IntrinsicElements
  >;
} & Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
  };

export const PhoneInput = ({
  name,
  locale,
  required = false,
  onChange,
  inputComponent,
  countrySelectComponent,
  placeholder,
  ...props
}: PhoneInputProps) => {
  const [country, setCountry] = useState<RPNInput.Country>("US");
  const resolvedPlaceholder =
    placeholder ?? (country === "US" ? "(264) 235-1234" : undefined);
  const { track } = useAnalytics();
  const isAutocompleting = useRef<boolean>(false);

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    /* Chromium, iOS Safari, and Android Chrome fire a keydown event with key "Unidentified",
     * but MacOS Safari sometimes fires a keydown event with key undefined, and sometimes
     * does not fire a keydown event -- this latter case will not be caught by this system,
     * but it should represent a very small slice of use cases */
    if (e.key === "Unidentified" || e.key === undefined) {
      isAutocompleting.current = true;
    }
  };

  const onInput: InputEventHandler<HTMLInputElement> = (e) => {
    if (isAutocompleting.current) {
      /* If the only part of the value that's missing is the + symbol, then we should
       * add that ourselves (because the + gets added by the input and won't fire an
       * error), but obviously two + symbols would be broken if it does get inserted */
      const newValue = `+${(e.target as HTMLInputElement)?.value}`.replaceAll(
        /\++/g,
        "+",
      );
      /* exclude when newValue is an empty string, because that would double the number of
       * failure events from iOS, whether there is an actual failure or not. If an empty
       * string is entered, then nothing was really autocompleted, was it? */
      if (newValue?.length) {
        let newValueIsValid = false;
        try {
          newValueIsValid = isValidPhoneNumber(newValue, null);
        } catch {}

        track({
          event: HallowAnalyticsEvent.AutocompletedPhoneNumber,
          properties: {
            inserted_value: newValue,
            inserted_value_is_valid: newValueIsValid,
          },
        });
      }

      isAutocompleting.current = false;
    }
  };

  return (
    <RPNInput.default
      css={baseStyle}
      autoComplete="tel"
      autoFocus
      defaultCountry={country}
      flagComponent={PhoneCountryFlag}
      countrySelectComponent={countrySelectComponent ?? PhoneCountrySelect}
      inputComponent={inputComponent ?? InputComponent}
      onChange={(value) => onChange?.(value)}
      onCountryChange={(country) => setCountry(country)}
      name={name}
      onInput={onInput}
      onKeyDown={onKeyDown}
      placeholder={resolvedPlaceholder}
      {...props}
    />
  );
};

PhoneInput.displayName = "PhoneInput";

const InputComponent = forwardRef<HTMLInputElement>((props, ref) => {
  return <TextInput css={inputStyle} {...props} ref={ref} />;
});
